import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import { Badge } from 'reactstrap';
import { useOpenNotificationsModal } from '../../lib/serviceWorkerHook';

import { Path, getLink } from '../../RoutePath';
import { useAuthentication } from '../Authentication/Authentication';
import { useProfile } from '../Profile/ProfileContext';
import { StudioStatus } from '../StudioSchedule/helper';
import { StudioButton, StudioButtonLeave } from './Button';
import { BuyMore } from './BuyMore';
import { CompleteProfile } from './CompleteProfile';
import { useStudio } from './Context';
import { DonatePoints } from './DonatePoints';
import { StudioGoLiveButton, StudioGoLiveStopButton } from './GoLive/Button';
import { StudioGoLiveCountdown } from './GoLive/Countdown';
import { useCurrentStudio } from './useCurrentStudio';

export const StudioHeader = ({ isStudioRoute, isSearchOpen }) => {
	const { t } = useTranslation();
	const { isLoggedIn } = useAuthentication();
	const openNotificationsModal = useOpenNotificationsModal();
	const {
		currentEstimation,
		isCurrentEstimationBellowLimit,
		isCurrentStudioStarted,
		isCurrentStudioMine,
		isCurrentStudioOperatedByMe,
		currentStudio,
	} = useCurrentStudio();
	const { controlroomActive } = useStudio();

	const showBuyPoints = (isCurrentStudioMine || isCurrentStudioOperatedByMe)
		&& !!currentEstimation
		&& isCurrentEstimationBellowLimit;

	const { profile } = useProfile();

	const isOperatorRoute = useRouteMatch({ path: getLink(
		Path.STUDIO_OPERATOR,
		{ hashtag: currentStudio?.owner?.hashtag, code: currentStudio?.code },
	) });

	const isStudioControlled = isCurrentStudioStarted
		&& (isCurrentStudioMine || (isCurrentStudioOperatedByMe && isOperatorRoute?.isExact));

	const isParticipantRoute = useRouteMatch({ path: getLink(
		Path.STUDIO_PARTICIPANT,
		{ hashtag: currentStudio?.owner?.hashtag, code: currentStudio?.code },
	) });
	const showLiveStatus = isStudioControlled || isParticipantRoute?.isExact;

	const isStudioRunning = currentStudio?.status === StudioStatus.RUNNING;
	const isLiveEnabled = isCurrentStudioStarted && currentStudio?.isLiveEnabled;
	const isLiveActive = isLiveEnabled && (currentStudio?.liveStatus === 'active' || currentStudio?.liveStatus === 'live_recording_started');
	const isRecording = isStudioRunning && currentStudio?.isRecording;
	const isLiveEnded = isStudioRunning && currentStudio?.isPublic
		&& currentStudio.hasLiveAlreadyStarted && !isLiveEnabled;
	const isGoLiveLoading = isLiveEnabled && !isLiveActive;
	const showGoLiveButton = isStudioRunning && controlroomActive && currentStudio?.isPublic
		&& isStudioControlled && !isLiveEnabled && !currentStudio.hasLiveAlreadyStarted;
	const showGoLiveStopButton = isStudioRunning && controlroomActive
		&& isStudioControlled && isLiveActive;
	const isRegularStudioRoute = isStudioRoute && !controlroomActive;

	useEffect(() => {
		if (isLoggedIn) {
			openNotificationsModal();
		}
	}, [isLoggedIn, openNotificationsModal]);

	return (
		<div
			className={clsx('StudioHeader d-flex align-items-center text-second h-100 flex-shrink-0 ml-auto',
				{ 'search-menu-open': isSearchOpen },
				{ 'search-menu-close': !isSearchOpen })}
		>
			{isCurrentStudioStarted && isRegularStudioRoute && (
				<StudioButtonLeave />
			)}
			{showBuyPoints && !profile?.completedRewardClaimed && <CompleteProfile />}
			{showBuyPoints && (
				<span className="d-none d-lg-block">
					{isCurrentStudioMine
						? <BuyMore />
						: <DonatePoints channel={currentStudio.owner} />}
				</span>
			)}
			{showLiveStatus && (
				<div className="d-flex align-items-center">
					{isLiveActive && (
						<Badge className="mr-2" color="danger" title={t('StudioHeader.onPublicLive')}>{t('StudioHeader.onAir')}</Badge>
					)}
					{isRecording && (
						<Badge className="mr-2" color="danger" title={t('StudioHeader.recording')}>{t('StudioHeader.rec')}</Badge>
					)}
					{isLiveEnded && (
						<Badge className="mr-2" color="info" title={t('StudioHeader.publicLiveEnded')}>{t('StudioHeader.publicLiveEnded')}</Badge>
					)}
					{showGoLiveButton && <StudioGoLiveButton />}
					{isGoLiveLoading && <StudioGoLiveCountdown />}
					{showGoLiveStopButton && <StudioGoLiveStopButton />}
				</div>
			)}
			{isLoggedIn && <StudioButton />}
		</div>
	);
};

StudioHeader.propTypes = {
	isSearchOpen: PropTypes.bool,
	isStudioRoute: PropTypes.bool,
};

StudioHeader.defaultProps = {
	isStudioRoute: false,
	isSearchOpen: false,
};
